import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { Subscription, Observable, EMPTY, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { ConfigService } from '../core/services/config.service';
import { TransService } from '../core/models/translation.model';
import { ResourceService } from '../core/services/resource.service';
import { SwapService } from '../core/services/swap.service';
import { UtilsService } from '../core/services/utils.service';
import { AuthService } from '../core/services/auth.service';
import { AuthMode } from '../core/models/dataContract.model';
import { StorageService } from '../core/services/storage.service';
import { HelperService } from '../core/services/helper.service';
import { HttpErrorResponse } from '@angular/common/module.d-CnjH8Dlt';

@Component({
  selector: 'app-splash',
  templateUrl: './splash.component.html',
  styleUrls: ['./splash.component.scss'],
  standalone: false
})
export class SplashComponent implements OnInit {
  sub: Subscription;

  imageSource = this.config.getConfigEx(
    'images:logoSplash',
    'assets/img/ocglogoblack.png'
  );
  imageHeight = this.config.getConfigEx('images:logoSplashHeight', 80);

  bgColor = this.config.getConfigEx('images:bgColorSplash', '#2f2f2e');
  bgColorLoading = this.config.getConfigEx(
    'images:bgColorSplashLoading',
    '#fffafa'
  );

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private config: ConfigService,
    private translate: TransService,
    private resource: ResourceService,
    private swap: SwapService,
    private utils: UtilsService,
    private auth: AuthService,
    private storage: StorageService,
    private helper: HelperService
  ) { }

  ngOnInit() {
    const startPath = this.config.getConfig('startPath', '/app');

    this.swap.editMode = false;

    const obs: Observable<Params> = this.resource
      .load(this.resource.accessConnection)
      .pipe(
        switchMap(() => {
          return this.resource.getCurrentUser().pipe(
            tap(() => {
              this.resource.customViewSetting = this.utils.parseComponentConfig(
                this.resource.customViewString
              );
              const cachedLang = this.storage.getItem(this.utils.localStorageLanguage);
              if (!cachedLang && this.resource.customViewSetting.language) {
                this.swap.currentLanguage =
                  this.resource.customViewSetting.language;
                this.translate.use(this.resource.customViewSetting.language);
              }
            })
          );
        }),
        switchMap(() => {
          if (this.config.getConfig('maintenanceMode', false)) {
            const loginExceptions: string[] = this.config.getConfig(
              'maintenanceExceptions',
              []
            );
            const loginAccount: string = this.utils.ExtraValue(
              this.resource.loginUser,
              'AccountName'
            );
            if (loginAccount) {
              const pos = loginExceptions.findIndex(
                (a: string) => a.toLowerCase() === loginAccount.toLowerCase()
              );
              if (pos < 0) {
                this.auth.logout(false, '/maintenance');
                return EMPTY;
              }
            } else {
              this.auth.logout(false, '/maintenance');
              return EMPTY;
            }
          }
          return this.resource.getUserConfig().pipe(
            tap(() => {
              this.resource.primaryViewSetting =
                this.utils.parseComponentConfig(
                  this.resource.primaryViewString
                );
            })
          );
        }),
        switchMap(() => {
          this.swap.broadcast({ name: 'resource-service-loaded' });

          if (this.auth.authMode === AuthMode.azure) {
            return this.resource.resetUserCache();
          }
          return of(null);
        }),
        switchMap(() => {
          if (this.auth.authMode === AuthMode.azure) {
            return this.resource.getSystemMessage();
          } else {
            return of('');
          }
        }),
        switchMap((msg: string) => {
          if (msg) {
            setTimeout(() => {
              this.swap.broadcast({
                name: 'system-message',
                parameter: msg,
              });
            }, this.config.getConfig('intervalLarge', 1000));
          }
          return this.route.queryParams;
        })
      );

    this.sub = obs
      .pipe(
        tap((params) => {
          const pathBeforeLoad = this.storage.getItem(this.utils.localStoragePathBeforeLoad);
          if (pathBeforeLoad) {
            this.storage.removeItem(this.utils.localStoragePathBeforeLoad);
            const routePrefix = this.config.getConfig('routePrefix', '');
            let path: string = routePrefix
              ? pathBeforeLoad.replace(routePrefix, '')
              : pathBeforeLoad;
            if (!path || path === 'login' || path === '/login') {
              path = startPath;
            } else {
              const pos = path.indexOf('/sidenav:');
              if (pos > 0) {
                path = path.substring(0, pos).replace('(', '');
              }
            }
            this.helper.navigateToPath(path);
          } else {
            this.helper.navigateToPath(this.helper.getStartPath(startPath));
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this.swap.setError(this.utils.getServiceError(err));
          this.router.navigate(['/error']);
          return EMPTY;
        })
      )
      .subscribe();
  }
}
